import axios from "axios";
const API_URL = process.env.REACT_APP_SERVER_URL;

export const getDashboardStats = async (token, { view = 'year', year, month } = {}) => {
  try {
    const params = new URLSearchParams();
    if (view) params.set('view', view);
    if (year != null) params.set('year', String(year));
    if (month != null) params.set('month', String(month));
    const qs = params.toString();
    const res = await fetch(`${API_URL}/admin/dashboard/stats${qs ? `?${qs}` : ''}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) throw new Error("Failed to fetch dashboard stats");
    return await res.json();
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    throw error;
  }
};

export const getUsers = async (token, page = 1, search = "", limit = 100, includeBookings = false) => {
  try {
    const params = new URLSearchParams({ page, limit, search });
    if (includeBookings) params.set('includeBookings', '1');
    const res = await fetch(
      `${API_URL}/admin/users?${params.toString()}`,
      {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      }
    );
    if (!res.ok) throw new Error("Failed to fetch users");
    return await res.json();
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const refreshUserRoles = async (token) => {
  const res = await fetch(`${API_URL}/admin/users/refresh-roles`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to refresh user roles");
  return res.json();
};

export const getUserDetails = async (token, userId) => {
  try {
    const res = await fetch(`${API_URL}/admin/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) throw new Error("Failed to fetch user details");
    return await res.json();
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};

export const grantCourseAccess = async (token, userId, courseId) => {
  const res = await fetch(`${API_URL}/admin/users/${userId}/courses`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ courseId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to grant course access");
  return data;
};

export const revokeCourseAccess = async (token, userId, purchaseId) => {
  const res = await fetch(`${API_URL}/admin/users/${userId}/courses/${purchaseId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to revoke course access");
  return data;
};

export const getMergePreview = async (token, { primaryUserId, secondaryUserId }) => {
  const res = await fetch(`${API_URL}/admin/users/merge-preview`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ primaryUserId, secondaryUserId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to load merge preview");
  return data;
};

export const mergeUsers = async (token, { primaryUserId, secondaryUserId, profile }) => {
  const res = await fetch(`${API_URL}/admin/users/merge`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ primaryUserId, secondaryUserId, profile }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Merge failed");
  return data;
};

export const createPlaceholderUser = async (token, payload) => {
  const res = await fetch(`${API_URL}/admin/users/placeholder`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to create placeholder user");
  return data;
};

export const createPasswordResetLink = async (token, userId) => {
  const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/password-reset-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to create password reset link');
  return data;
};

export const updateAdminUser = async (token, userId, updates) => {
  const res = await fetch(`${API_URL}/admin/users/${userId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error("Failed to update user");
  return res.json();
};

export const resetUserNameToAuth = async (token, userId) =>
  updateAdminUser(token, userId, { resetNameToAuth: true });

export const addPassToUser = async (token, { userId, passTypeId, daysValid, notes, paid, isAdmin, finalPrice }) => {
  try {
    const res = await fetch(`${API_URL}/admin/users/${userId}/add-pass`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, paid, passTypeId, daysValid, notes, isAdmin, finalPrice }),
    });
    if (!res.ok) throw new Error("Failed to add pass");
    return await res.json();
  } catch (error) {
    console.error("Error adding pass:", error);
    throw error;
  }
};

export const patchAdminBooking = async (token, classId, bookingId, patch) => {
  const res = await fetch(`${API_URL}/admin/bookings/${classId}/${bookingId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to update booking");
  }
  return res.json();
};

export const updateBookingOnline = (token, classId, bookingId, { online }) =>
  patchAdminBooking(token, classId, bookingId, { online });

export const updateBookingAttended = (token, classId, bookingId, { attended }) =>
  patchAdminBooking(token, classId, bookingId, { attended });

export const getClasses = async (token, page = 1, runAutoBook = false) => {
  try {
    const params = new URLSearchParams({ page });
    if (runAutoBook) params.set('runAutoBook', '1');
    const res = await fetch(`${API_URL}/admin/classes?${params}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) throw new Error("Failed to fetch classes");
    return await res.json();
  } catch (error) {
    console.error("Error fetching classes:", error);
    throw error;
  }
};

export const getSubscriptions = async (token, page = 1, includeTest = false, status = '', passTypeId = '') => {
  try {
    const params = new URLSearchParams({ page });
    if (includeTest) params.set('includeTest', 'true');
    if (status) params.set('status', status);
    if (passTypeId) params.set('passTypeId', passTypeId);
    const res = await fetch(`${API_URL}/admin/subscriptions?${params}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) throw new Error("Failed to fetch subscriptions");
    return await res.json();
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    throw error;
  }
};

export const getPasses = async (
  token,
  {
    page = 1,
    limit = 20,
    status = '',
    includeTest = false,
    paid = '',
    passTypeId = '',
    purchasedFrom = '',
    purchasedTo = '',
  } = {}
) => {
  const params = new URLSearchParams({ page, limit });
  if (status) params.set('status', status);
  if (includeTest) params.set('includeTest', 'true');
  if (paid) params.set('paid', paid);
  if (passTypeId) params.set('passTypeId', passTypeId);
  if (purchasedFrom) params.set('purchasedFrom', purchasedFrom);
  if (purchasedTo) params.set('purchasedTo', purchasedTo);
  const res = await fetch(`${API_URL}/admin/passes?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch passes");
  return res.json();
};

export const refreshPassesStatus = async (token) => {
  const res = await fetch(`${API_URL}/admin/passes/refresh-status`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to refresh pass statuses");
  return res.json();
};

export const addStudentToClass = async (token, classId, userId, { online } = {}) => {
  const res = await axios.post(
    `${API_URL}/admin/classes/${classId}/book-for-user`,
    { userId, online: online === true },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const addToWaitlist = async (token, classId, userId) => {
  const res = await fetch(`${API_URL}/admin/classes/${classId}/waitlist`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to add to waiting list");
  }
  return res.json();
};

export const removeFromWaitlist = async (token, classId, userId) => {
  const res = await fetch(`${API_URL}/admin/classes/${classId}/waitlist`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to remove from waiting list");
  }
  return res.json();
};

export const removeStudentFromClass = async (token, classId, userId) => {
  const res = await axios.post(
    `${API_URL}/admin/classes/${classId}/cancel-for-user`,
    { userId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const createClass = async (token, classData) => {
  const res = await axios.post(
    `${API_URL}/admin/classes`,
    classData,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const deleteClass = async (token, classId) => {
  const res = await axios.delete(
    `${API_URL}/admin/classes/${classId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const updateClass = async (token, classId, classData) => {
  const res = await axios.put(
    `${API_URL}/admin/classes/${classId}`,
    classData,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const updatePass = async (token, passId, data) => {
  const res = await axios.put(
    `${API_URL}/passes/${passId}`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const deletePass = async (token, passId) => {
  const res = await axios.delete(
    `${API_URL}/passes/${passId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getRevenue = async (token, year, { month } = {}) => {
  const params = new URLSearchParams();
  params.set('year', String(year));
  if (month != null) params.set('month', String(month));
  const res = await axios.get(`${API_URL}/admin/revenue?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// --- Business expenses ---
export const getExpenses = async (token, { year, month, category } = {}) => {
  const params = new URLSearchParams();
  if (year != null) params.set('year', year);
  if (month != null) params.set('month', month);
  if (category) params.set('category', category);
  const q = params.toString();
  const res = await fetch(`${API_URL}/admin/expenses${q ? `?${q}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch expenses');
  return res.json();
};

export const createExpense = async (token, { amount, currency, category, date, notes }) => {
  const res = await fetch(`${API_URL}/admin/expenses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ amount, currency: currency || 'ILS', category: category || 'other', date: date || new Date().toISOString().slice(0, 10), notes: notes || '' }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to create expense');
  }
  return res.json();
};

export const updateExpense = async (token, id, { amount, currency, category, date, notes }) => {
  const res = await fetch(`${API_URL}/admin/expenses/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ amount, currency, category, date, notes }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to update expense');
  }
  return res.json();
};

export const deleteExpense = async (token, id) => {
  const res = await fetch(`${API_URL}/admin/expenses/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to delete expense');
  }
  return res.json();
};

export const getVideoAdminMeta = async (token) => {
  const res = await fetch(`${API_URL}/admin/videos/meta`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch video metadata");
  return res.json();
};

export const getAdminPracticeVideos = async (token) => {
  const res = await fetch(`${API_URL}/admin/videos/practice`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch practice videos");
  return res.json();
};

export const createAdminPracticeVideo = async (token, payload) => {
  const res = await fetch(`${API_URL}/admin/videos/practice`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to create practice video");
  return data;
};

export const updateAdminPracticeVideo = async (token, id, payload) => {
  const res = await fetch(`${API_URL}/admin/videos/practice/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to update practice video");
  return data;
};

export const deleteAdminPracticeVideo = async (token, id) => {
  const res = await fetch(`${API_URL}/admin/videos/practice/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to delete practice video");
  return data;
};

export const getAdminConstructorVideos = async (token) => {
  const res = await fetch(`${API_URL}/admin/videos/constructor`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch constructor videos");
  return res.json();
};

export const createAdminConstructorVideo = async (token, payload) => {
  const res = await fetch(`${API_URL}/admin/videos/constructor`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to create constructor video");
  return data;
};

export const updateAdminConstructorVideo = async (token, id, payload) => {
  const res = await fetch(`${API_URL}/admin/videos/constructor/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to update constructor video");
  return data;
};

export const deleteAdminConstructorVideo = async (token, id) => {
  const res = await fetch(`${API_URL}/admin/videos/constructor/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to delete constructor video");
  return data;
};

export const getAdminLiveVideos = async (token) => {
  const res = await fetch(`${API_URL}/admin/videos/live`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch live videos");
  return res.json();
};

export const createAdminLiveVideo = async (token, payload) => {
  const res = await fetch(`${API_URL}/admin/videos/live`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to create live video");
  return data;
};

export const updateAdminLiveVideo = async (token, id, payload) => {
  const res = await fetch(`${API_URL}/admin/videos/live/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to update live video");
  return data;
};

export const deleteAdminLiveVideo = async (token, id) => {
  const res = await fetch(`${API_URL}/admin/videos/live/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to delete live video");
  return data;
};

// jwtCheck rejections render as an HTML error page (no JSON body), so a 401/403 here
// would otherwise surface as a misleading generic "Failed to ..." message.
const poseErrorMessage = (res, data, fallback) => {
  if (res.status === 401 || res.status === 403) {
    return "Сессия истекла — обновите страницу и попробуйте снова";
  }
  return data.error || fallback;
};

export const getAdminPoses = async (token) => {
  const res = await fetch(`${API_URL}/admin/poses`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(poseErrorMessage(res, data, "Failed to fetch poses"));
  return data;
};

export const createAdminPose = async (token, payload) => {
  const res = await fetch(`${API_URL}/admin/poses`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(poseErrorMessage(res, data, "Failed to create pose"));
  return data;
};

export const updateAdminPose = async (token, id, payload) => {
  const res = await fetch(`${API_URL}/admin/poses/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(poseErrorMessage(res, data, "Failed to update pose"));
  return data;
};

export const deleteAdminPose = async (token, id) => {
  const res = await fetch(`${API_URL}/admin/poses/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(poseErrorMessage(res, data, "Failed to delete pose"));
  return data;
};

export const deleteCloudinaryImage = async (token, publicId) => {
  const res = await fetch(`${API_URL}/admin/cloudinary/image`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ publicId }),
  });
  return res.json().catch(() => ({}));
};

// --- Admin: Courses ---

export const getAdminCourses = async (token) => {
  const res = await axios.get(`${API_URL}/admin/courses`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const createAdminCourse = async (token, payload) => {
  const res = await axios.post(`${API_URL}/admin/courses`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateAdminCourse = async (token, courseId, payload) => {
  const res = await axios.put(`${API_URL}/admin/courses/${courseId}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const deleteAdminCourse = async (token, courseId) => {
  const res = await axios.delete(`${API_URL}/admin/courses/${courseId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getAdminCoursePurchases = async (token, courseId) => {
  const res = await axios.get(`${API_URL}/admin/courses/${courseId}/purchases`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const createAdminCourseClass = async (token, courseId, payload) => {
  const res = await axios.post(`${API_URL}/admin/courses/${courseId}/classes`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateAdminCourseClass = async (token, courseId, classId, payload) => {
  const res = await axios.put(`${API_URL}/admin/courses/${courseId}/classes/${classId}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const deleteAdminCourseClass = async (token, courseId, classId) => {
  const res = await axios.delete(`${API_URL}/admin/courses/${courseId}/classes/${classId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// --- Admin: Pass types ---

export const getAdminPassTypes = async (token) => {
  const res = await axios.get(`${API_URL}/admin/pass-types`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const createAdminPassType = async (token, payload) => {
  const res = await axios.post(`${API_URL}/admin/pass-types`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateAdminPassType = async (token, passTypeId, payload) => {
  const res = await axios.put(`${API_URL}/admin/pass-types/${passTypeId}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const deleteAdminPassType = async (token, passTypeId) => {
  const res = await axios.delete(`${API_URL}/admin/pass-types/${passTypeId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// --- Admin: Class types ---

export const getAdminClassTypes = async (token) => {
  const res = await axios.get(`${API_URL}/admin/class-types`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const createAdminClassType = async (token, payload) => {
  const res = await axios.post(`${API_URL}/admin/class-types`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateAdminClassType = async (token, classTypeId, payload) => {
  const res = await axios.put(`${API_URL}/admin/class-types/${classTypeId}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const deleteAdminClassType = async (token, classTypeId) => {
  const res = await axios.delete(`${API_URL}/admin/class-types/${classTypeId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getAdminFolders = async (token) => {
  const res = await fetch(`${API_URL}/admin/folders`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch folders");
  return res.json();
};

export const createAdminFolder = async (token, payload) => {
  const res = await fetch(`${API_URL}/admin/folders`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to create folder");
  return data;
};

export const updateAdminFolder = async (token, id, payload) => {
  const res = await fetch(`${API_URL}/admin/folders/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to update folder");
  return data;
};

export const deleteAdminFolder = async (token, id) => {
  const res = await fetch(`${API_URL}/admin/folders/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to delete folder");
  return data;
};
